import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import styles from "./FleetMonitor.module.css";
import useTheme from "../hooks/useTheme.ts";
import {
  FiTruck,
  FiUser,
  FiMapPin,
  FiDroplet,
  FiActivity,
  FiBox,
  FiRadio,
} from "react-icons/fi";
import { GiOilRig } from "react-icons/gi";

interface PumpData {
  pump_name: string;
  station: number;
  gear: number;
  throttle: number;
  engine_load: number;
  rpm: number;
  calculated_discharge_rate: number;
  [key: string]: any;
}

interface ColumnConfig {
  key: string;
  label: string;
  field: string;
  removable: boolean;
}

interface AvailableChannel {
  value: string;
  label: string;
}

interface TreatmentData {
  clean_total_meas: number;
  slurry_total: number;
  slurry_total_calc: number;
  sand_rate_surface: number;
  density_surface: number;
  treating_pressure: number;
  hp: number;
  pump_timestamp?: number;
  [key: string]: any;
}

interface PumpConfig {
  pump_name: string;
  van_id: string;
  engine_type: string;
  trans_type: string;
  dgb_type: string;
  [key: string]: any;
}

interface CustomerWellResult {
  fleet_id: number;
  pad: {
    id: number;
    job_number: number;
    pad_name: string;
    well_count: number;
    stage_count: number;
    start_ts: number;
    end_ts: number | null;
    status: string;
  } | null;
  organization: {
    id: number;
    name: string;
  } | null;
  well: {
    id: number;
    well_name: string;
    api: string;
  } | null;
  stage: {
    id: number;
    stage_number: number;
    start_ts: number;
  } | null;
  reason: string;
}

interface CustomerWellData {
  fleet_count: number;
  results: CustomerWellResult[];
}

interface FleetMonitorProps {
  fleet?: number;
  externalPumps?: PumpData[];
  externalTreatment?: TreatmentData | null;
  externalPumpTimestamp?: number | null;
  externalCustomerWell?: CustomerWellData | null;
  externalVanId?: string | null;
  externalPumpConfigs?: Map<string, PumpConfig>;
  useExternalData?: boolean;
  visibleColumns?: ColumnConfig[];
  onColumnsChange?: (columns: ColumnConfig[]) => void;
}

const AVAILABLE_CHANNELS: AvailableChannel[] = [
  { value: "engine_hours", label: "Engine Hours" },
  { value: "transmission_hours", label: "Transmission Hours" },
  { value: "pe_hours", label: "PE Hours" },
  { value: "fe_hours", label: "FE Hours" },
  { value: "rpm", label: "RPM" },
  { value: "gear", label: "Gear" },
  { value: "fuel_rate", label: "Fuel Rate" },
  { value: "engine_load", label: "Engine Load" },
  { value: "dual_fuel_ratio", label: "Dual Fuel Ratio" },
  { value: "inst_fuel_disp", label: "Inst Fuel Disp" },
  { value: "fan_speed", label: "Fan Speed" },
  { value: "trans_lockup", label: "Trans Lockup" },
  { value: "discharge_pressure", label: "Discharge Pressure" },
  { value: "engine_temp", label: "Engine Temp" },
  { value: "engine_oil_pressure", label: "Engine Oil Pressure" },
  { value: "trans_temp", label: "Trans Temp" },
  { value: "trans_pressure", label: "Trans Pressure" },
  { value: "pe_temp", label: "PE Temp" },
  { value: "pe_pressure", label: "PE Pressure" },
  { value: "throttle", label: "Throttle" },
  { value: "voltage", label: "Voltage" },
  { value: "calculated_discharge_rate", label: "Calculated Discharge Rate" },
  { value: "engine_air_filter_diff", label: "Engine Air Filter Diff" },
  { value: "crankcase_pressure", label: "Crankcase Pressure" },
  { value: "engine_fuel_pressure", label: "Engine Fuel Pressure" },
  { value: "grease_active", label: "Grease Active" },
  { value: "fault_spn_1", label: "Fault SPN 1" },
  { value: "fault_fmi_1", label: "Fault FMI 1" },
  { value: "fault_spn_2", label: "Fault SPN 2" },
  { value: "fault_fmi_2", label: "Fault FMI 2" },
  { value: "fault_spn_3", label: "Fault SPN 3" },
  { value: "fault_fmi_3", label: "Fault FMI 3" },
  { value: "fault_spn_4", label: "Fault SPN 4" },
  { value: "fault_fmi_4", label: "Fault FMI 4" },
  { value: "fault_spn_5", label: "Fault SPN 5" },
  { value: "fault_fmi_5", label: "Fault FMI 5" },
  { value: "station", label: "Station" },

  // Electric fleet channels
  { value: "discharge_vfd_amps", label: "VFD Amps" },
  {
    value: "discharge_vfd_dc_link_volts",
    label: "DC Link Volts",
  },
  {
    value: "discharge_motor_winding_a1_temp",
    label: "Winding A1 Temp",
  },
  {
    value: "discharge_motor_winding_a2_temp",
    label: "Winding A2 Temp",
  },
  {
    value: "discharge_motor_winding_b1_temp",
    label: "Winding B1 Temp",
  },
  {
    value: "discharge_motor_winding_b2_temp",
    label: "Winding B2 Temp",
  },
  {
    value: "discharge_motor_winding_c1_temp",
    label: "Winding C1 Temp",
  },
  {
    value: "discharge_motor_winding_c2_temp",
    label: "Winding C2 Temp",
  },
  {
    value: "discharge_motor_de_bearing_temp",
    label: "DE Bearing Temp",
  },
  {
    value: "discharge_motor_nde_bearing_temp",
    label: "NDE Bearing Temp",
  },
  { value: "vfd_speed_hz", label: "VFD Speed Hz" },
  { value: "vfd_speed_percent", label: "VFD Speed %" },
  { value: "vfd_power", label: "VFD Power" },
];

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: "pump_name", label: "Unit", field: "pump_name", removable: false },
  { key: "station", label: "Stn", field: "station", removable: true },
  { key: "gear", label: "Gear", field: "gear", removable: true },
  { key: "throttle", label: "Thrtl", field: "throttle", removable: true },
  { key: "engine_load", label: "Load", field: "engine_load", removable: true },
  { key: "rpm", label: "RPM", field: "rpm", removable: true },
];

const MAX_COLUMNS = 11;
interface InfoRowItemProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}

const InfoRowItem: React.FC<InfoRowItemProps> = ({ icon, label, value }) => {
  return (
    <>
      <span className={styles.infoLabel}>
        {icon}
        {label}
      </span>
      <span className={styles.infoValue}>{value}</span>
    </>
  );
};

const FleetMonitor: React.FC<FleetMonitorProps> = ({
  fleet: propFleet,
  externalPumps,
  externalTreatment,
  externalPumpTimestamp,
  externalCustomerWell,
  externalVanId,
  externalPumpConfigs,
  useExternalData = false,
  visibleColumns: externalVisibleColumns,
  onColumnsChange,
}) => {
  const [searchParams] = useSearchParams();
  const fleetParam = searchParams.get("fleet");
  const fleet = propFleet ?? (fleetParam ? parseInt(fleetParam) : null);

  const [pumps, setPumps] = useState<PumpData[]>([]);
  const [treatment, setTreatment] = useState<TreatmentData | null>(null);
  const [pumpConfigs, setPumpConfigs] = useState<Map<string, PumpConfig>>(
    new Map(),
  );
  const [customerWell, setCustomerWell] = useState<CustomerWellData | null>(
    null,
  );
  const [vanId, setVanId] = useState<string | null>(null);
  const [pumpTimestamp, setPumpTimestamp] = useState<number | null>(null);

  // Column management state
  const [columns, setColumns] = useState<ColumnConfig[]>(
    externalVisibleColumns || DEFAULT_COLUMNS,
  );
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>({ field: "station", direction: "asc" });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Tooltip state
  const [hoveredPump, setHoveredPump] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const { theme } = useTheme();
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

  const pumpDataInterval = useRef<NodeJS.Timeout | null>(null);
  const customerWellInterval = useRef<NodeJS.Timeout | null>(null);
  const pumpConfigInterval = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLTableCellElement | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);

  // Sync external columns if provided
  useEffect(() => {
    if (externalVisibleColumns) {
      setColumns(externalVisibleColumns);
    }
  }, [externalVisibleColumns]);

  // Handle column changes
  const handleColumnsChange = (newColumns: ColumnConfig[]) => {
    setColumns(newColumns);
    if (onColumnsChange) {
      onColumnsChange(newColumns);
    }
  };

  // Remove column
  const removeColumn = (columnKey: string) => {
    const newColumns = columns.filter((col) => col.key !== columnKey);
    handleColumnsChange(newColumns);
  };

  // Add column
  const addColumn = (channelValue: string) => {
    const channel = AVAILABLE_CHANNELS.find((ch) => ch.value === channelValue);
    if (channel && columns.length < MAX_COLUMNS) {
      const newColumn: ColumnConfig = {
        key: channel.value,
        label: channel.label,
        field: channel.value,
        removable: true,
      };
      handleColumnsChange([...columns, newColumn]);
      setShowColumnDropdown(false);
    }
  };

  const getAvailableChannels = (): AvailableChannel[] => {
    const selectedFields = columns.map((col) => col.field);

    const priorityChannels = [
      "calculated_discharge_rate",
      "discharge_vfd_amps",
      "discharge_vfd_dc_link_volts",
      "discharge_motor_winding_a1_temp",
      "discharge_motor_winding_a2_temp",
      "discharge_motor_winding_b1_temp",
      "discharge_motor_winding_b2_temp",
      "discharge_motor_winding_c1_temp",
      "discharge_motor_winding_c2_temp",
      "discharge_motor_de_bearing_temp",
      "discharge_motor_nde_bearing_temp",
      "vfd_speed_hz",
      "vfd_speed_percent",
      "vfd_power",
    ];

    return AVAILABLE_CHANNELS.filter(
      (channel) => !selectedFields.includes(channel.value),
    ).sort((a, b) => {
      const aIndex = priorityChannels.indexOf(a.value);
      const bIndex = priorityChannels.indexOf(b.value);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return a.label.localeCompare(b.label);
    });
  };

  const handleSort = (field: string) => {
    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.field === field &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ field, direction });
  };

  const getSortedPumps = () => {
    if (!sortConfig) return pumps;

    return [...pumps].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      const aString = String(aValue);
      const bString = String(bValue);
      return sortConfig.direction === "asc"
        ? aString.localeCompare(bString)
        : bString.localeCompare(aString);
    });
  };

  const toggleDropdown = () => {
    if (!showColumnDropdown && addButtonRef.current) {
      const rect = addButtonRef.current.getBoundingClientRect();

      const dropdownWidth = 260;
      const dropdownHeight = 260;
      const gap = 6;

      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < dropdownHeight;

      setDropdownPosition({
        top: openAbove ? rect.top - dropdownHeight - gap : rect.bottom + gap,
        left: Math.min(rect.left, window.innerWidth - dropdownWidth - 16),
      });
    }
    setShowColumnDropdown(!showColumnDropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        addButtonRef.current &&
        !addButtonRef.current.contains(event.target as Node)
      ) {
        setShowColumnDropdown(false);
      }
    };

    if (showColumnDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showColumnDropdown]);

  const fetchPumpData = useCallback(async () => {
    if (useExternalData) return;
    if (!fleet) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${backendUrl}/latest-treatment-pump-data-multi?fleets=${fleet}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data[fleet];

      if (data?.pumps) {
        setPumps(data.pumps);
      }

      if (data?.treatment) {
        setTreatment(data.treatment);
      }

      if (data?.pump_timestamp) {
        setPumpTimestamp(data.pump_timestamp);
      }
    } catch (error) {
      console.error("Error fetching pump data:", error);
    }
  }, [fleet, backendUrl, externalPumps]);

  const fetchCustomerWell = useCallback(async () => {
    if (!fleet) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${backendUrl}/current_customer_well`,
        {
          fleet_ids: [fleet],
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCustomerWell(response.data);
    } catch (error) {
      console.error("Error fetching customer/well data:", error);
    }
  }, [fleet, backendUrl]);

  const fetchPumpConfigsFromInterval = useCallback(async () => {
    if (!fleet) return;

    setPumps((currentPumps) => {
      if (currentPumps.length === 0) return currentPumps;

      const pumpNames = currentPumps.map((p: PumpData) => p.pump_name);
      if (pumpNames.length === 0) return currentPumps;

      const token = localStorage.getItem("token");
      axios
        .post(
          `${backendUrl}/pump-config/latest`,
          {
            pump_names: pumpNames,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        .then((response) => {
          const configs = new Map<string, PumpConfig>();
          const vanIdCounts = new Map<string, number>();

          response.data.configs?.forEach((config: PumpConfig) => {
            configs.set(config.pump_name, config);

            if (config.van_id) {
              const count = vanIdCounts.get(config.van_id) || 0;
              vanIdCounts.set(config.van_id, count + 1);
            }
          });

          setPumpConfigs(configs);

          if (vanIdCounts.size > 0) {
            let mostCommonVanId: string | null = null;
            let maxCount = 0;
            vanIdCounts.forEach((count, vanId) => {
              if (count > maxCount) {
                maxCount = count;
                mostCommonVanId = vanId;
              }
            });
            setVanId(mostCommonVanId);
          }
        })
        .catch((error) => {
          console.error("Error fetching pump configs:", error);
        });

      return currentPumps;
    });
  }, [fleet, backendUrl]);

  const stopPolling = useCallback(() => {
    if (pumpDataInterval.current) {
      clearInterval(pumpDataInterval.current);
      pumpDataInterval.current = null;
    }
    if (customerWellInterval.current) {
      clearInterval(customerWellInterval.current);
      customerWellInterval.current = null;
    }
    if (pumpConfigInterval.current) {
      clearInterval(pumpConfigInterval.current);
      pumpConfigInterval.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();

    if (!useExternalData) {
      fetchCustomerWell();
    }

    if (!useExternalData) {
      fetchPumpData();
      pumpDataInterval.current = setInterval(() => {
        fetchPumpData();
      }, 1000);

      customerWellInterval.current = setInterval(() => {
        fetchCustomerWell();
        fetchPumpConfigsFromInterval();
      }, 60000);
    }
  }, [
    stopPolling,
    fetchPumpData,
    fetchCustomerWell,
    fetchPumpConfigsFromInterval,
    fleet,
    useExternalData,
  ]);

  useEffect(() => {
    if (fleet) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [fleet, startPolling, stopPolling]);

  useEffect(() => {
    if (externalPumps !== undefined) {
      setPumps(externalPumps);
    }
  }, [externalPumps]);

  useEffect(() => {
    if (externalTreatment !== undefined) {
      setTreatment(externalTreatment);
    }
  }, [externalTreatment]);

  useEffect(() => {
    if (externalPumpTimestamp !== undefined) {
      setPumpTimestamp(externalPumpTimestamp);
    }
  }, [externalPumpTimestamp]);

  useEffect(() => {
    if (externalCustomerWell !== undefined) {
      setCustomerWell(externalCustomerWell);
    }
  }, [externalCustomerWell]);

  useEffect(() => {
    if (externalVanId !== undefined) {
      setVanId(externalVanId);
    }
  }, [externalVanId]);

  useEffect(() => {
    if (externalPumpConfigs !== undefined) {
      setPumpConfigs(externalPumpConfigs);
    }
  }, [externalPumpConfigs]);

  useEffect(() => {
    if (useExternalData) return;

    if (pumps.length > 0 && fleet) {
      const hasConfigs = pumps.some((p) => pumpConfigs.has(p.pump_name));
      if (!hasConfigs) {
        fetchPumpConfigsFromInterval();
      }
    }
  }, [pumps.length, fleet, useExternalData]);

  const getPumpRowColor = (pump: PumpData): string => {
    if (pump.gear === 0 && pump.rpm === 0) {
      return "#dc3545";
    }
    if (pump.gear === 0 && pump.rpm > 25) {
      return "#3b82f6";
    }
    return "#7bc342";
  };

  const getPumpStatus = (pump: PumpData): string => {
    if (pump.gear === 0 && pump.rpm === 0) {
      return "Not Running";
    }
    if (pump.gear === 0 && pump.rpm > 25) {
      return "Running/Not Pumping";
    }
    return "Running/Pumping";
  };

  const handleMouseEnter = (pumpName: string, event: React.MouseEvent) => {
    setHoveredPump(pumpName);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (hoveredPump) {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleMouseLeave = () => {
    setHoveredPump(null);
  };

  const getStatus = (): string => {
    if (treatment && treatment.slurry_total > 5) {
      return "In Progress";
    }
    return "Inactive";
  };

  const calculateMetrics = () => {
    const inline = pumps.length;
    const pumping = pumps.filter((p) => {
      if (p.gear === 0 && p.rpm === 0) return false;
      if (p.gear === 0 && p.rpm > 25) return false;
      return true;
    }).length;
    const expected = treatment ? +(treatment.hp / 1575).toFixed(0) || 0 : 0;

    return { inline, pumping, expected };
  };

  const getPumpingBoxClass = (): string => {
    const { pumping, expected } = calculateMetrics();

    if (expected === 0) {
      return styles.metric;
    }

    const difference = Math.abs(pumping - expected);
    const percentageDiff = (difference / expected) * 100;

    if (percentageDiff > 30) {
      return styles.metricDanger;
    } else if (percentageDiff >= 15) {
      return styles.metricWarning;
    }

    return styles.metric;
  };

  const isDataStale = (): boolean => {
    if (!pumpTimestamp) return false;

    const currentTimeUTC = Date.now();
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
    const dataAge = currentTimeUTC - pumpTimestamp;

    return dataAge >= twentyFourHoursInMs;
  };

  const metrics = calculateMetrics();
  const currentFleetInfo = customerWell?.results?.find(
    (item) => item.fleet_id === fleet,
  );

  if (!fleet) {
    return (
      <div
        className={`${styles.container} ${theme === "dark" ? "dark-theme" : "light-theme"}`}
      >
        <div className={styles.error}>
          <h2>No Fleet Selected</h2>
          <p>Please add ?fleet=X to the URL</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${theme === "dark" ? "dark-theme" : "light-theme"}`}
    >
      <div className={styles.metrics}>
        <div className={styles.statusBox}>
          <span className={styles.statusLabel}>Status:</span>
          <span
            className={`${styles.statusValue} ${getStatus() === "In Progress" ? styles.statusActive : styles.statusInactive}`}
          >
            {getStatus()}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Inline</span>
          <span className={styles.metricValue}>{metrics.inline}</span>
        </div>
        <div className={getPumpingBoxClass()}>
          <span className={styles.metricLabel}>Pumping</span>
          <span className={styles.metricValue}>{metrics.pumping}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Expected</span>
          <span className={styles.metricValue}>{metrics.expected}</span>
        </div>
      </div>

      <div className={styles.infoGrid}>
        <div className={styles.leftColumn}>
          <div className={styles.infoRow}>
            <InfoRowItem
              icon={<FiTruck className={styles.icon} />}
              label="Van:"
              value={vanId || "N/A"}
            />
          </div>

          <div className={styles.infoRow}>
            <InfoRowItem
              icon={<FiUser className={styles.icon} />}
              label="Customer:"
              value={customerWell?.results?.[0]?.organization?.name || "N/A"}
            />
          </div>

          <div className={styles.infoRow}>
            <InfoRowItem
              icon={<FiMapPin className={styles.icon} />}
              label="Pad:"
              value={currentFleetInfo?.pad?.pad_name || "N/A"}
            />
          </div>

          <div className={styles.infoRow}>
            <InfoRowItem
              icon={<GiOilRig className={styles.icon} />}
              label="Well:"
              value={currentFleetInfo?.well?.well_name || "N/A"}
            />
          </div>
        </div>

        <div className={styles.divider}></div>

        <div className={styles.rightColumn}>
          <div className={styles.infoRow}>
            <InfoRowItem
              icon={
                <FiDroplet className={`${styles.icon} ${styles.iconBlue}`} />
              }
              label="Clean Rate:"
              value={`${treatment?.clean_total_meas?.toFixed(2) || "0.00"} BPM`}
            />
          </div>

          <div className={styles.infoRow}>
            <InfoRowItem
              icon={
                <FiRadio className={`${styles.icon} ${styles.iconGreen}`} />
              }
              label="Slurry Rate:"
              value={`${treatment?.slurry_total?.toFixed(2) || "0.00"} BPM`}
            />
          </div>

          <div className={styles.infoRow}>
            <InfoRowItem
              icon={<FiBox className={`${styles.icon} ${styles.iconBrown}`} />}
              label="Sand Rate:"
              value={`${treatment?.sand_rate_surface?.toFixed(2) || "0.00"} lbs/min`}
            />
          </div>

          <div className={styles.infoRow}>
            <InfoRowItem
              icon={
                <FiActivity className={`${styles.icon} ${styles.iconRed}`} />
              }
              label="STP:"
              value={`${treatment?.treating_pressure?.toFixed(2) || "0.00"} PSI`}
            />
          </div>
        </div>
      </div>
      {isDataStale() ? (
        <div className={styles.noData}>
          <svg
            className={styles.noDataIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="12" cy="12" r="10" strokeWidth="2" />
            <line
              x1="12"
              y1="8"
              x2="12"
              y2="12"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="12" cy="16" r="1" fill="currentColor" />
          </svg>
          <h3>No Recent Data</h3>
          <p>Data is more than 24 hours old</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={styles.sortableHeader}>
                    <div className={styles.headerContent}>
                      <span
                        onClick={() => handleSort(column.field)}
                        className={styles.headerText}
                      >
                        {column.label}
                        {sortConfig && sortConfig.field === column.field && (
                          <span className={styles.sortIcon}>
                            {sortConfig.direction === "asc" ? " ▲" : " ▼"}
                          </span>
                        )}
                      </span>
                      {column.removable && (
                        <button
                          onClick={() => removeColumn(column.key)}
                          className={styles.removeColumnBtn}
                          title="Remove column"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </th>
                ))}
                {columns.length < MAX_COLUMNS && (
                  <th className={styles.addColumnHeader}>
                    <div className={styles.headerContent}>
                      <button
                        ref={addButtonRef}
                        onClick={toggleDropdown}
                        className={styles.addColumnBtn}
                      >
                        + Add Column ▼
                      </button>
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {getSortedPumps().map((pump, index) => (
                <tr
                  key={index}
                  style={{ backgroundColor: getPumpRowColor(pump) }}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={
                        column.key === "pump_name" ? styles.pumpName : ""
                      }
                      onMouseEnter={
                        column.key === "pump_name"
                          ? (e) => handleMouseEnter(pump.pump_name, e)
                          : undefined
                      }
                      onMouseMove={
                        column.key === "pump_name" ? handleMouseMove : undefined
                      }
                      onMouseLeave={
                        column.key === "pump_name"
                          ? handleMouseLeave
                          : undefined
                      }
                    >
                      {pump[column.field] !== null &&
                      pump[column.field] !== undefined
                        ? typeof pump[column.field] === "number"
                          ? Number.isInteger(pump[column.field])
                            ? pump[column.field]
                            : pump[column.field].toFixed(2)
                          : pump[column.field]
                        : "N/A"}
                    </td>
                  ))}
                  {columns.length < MAX_COLUMNS && <td></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Column Dropdown Portal */}
      {showColumnDropdown && (
        <div
          ref={dropdownRef}
          className={styles.columnDropdown}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {getAvailableChannels().map((channel) => (
            <div
              key={channel.value}
              onClick={() => addColumn(channel.value)}
              className={styles.dropdownItem}
            >
              {channel.label}
            </div>
          ))}
        </div>
      )}

      {/* Tooltip */}
      {hoveredPump && pumpConfigs.has(hoveredPump) && (
        <div
          className={styles.tooltip}
          style={{
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y + 15}px`,
          }}
        >
          <div className={styles.tooltipTitle}>{hoveredPump}</div>
          <div className={styles.tooltipItem}>
            <span>Status:</span>
            <span>
              {getPumpStatus(pumps.find((p) => p.pump_name === hoveredPump)!)}
            </span>
          </div>
          <div className={styles.tooltipItem}>
            <span>Engine Type:</span>
            <span>{pumpConfigs.get(hoveredPump)?.engine_type || "N/A"}</span>
          </div>
          <div className={styles.tooltipItem}>
            <span>Transmission Type:</span>
            <span>{pumpConfigs.get(hoveredPump)?.trans_type || "N/A"}</span>
          </div>
          <div className={styles.tooltipItem}>
            <span>Dual Fuel Type:</span>
            <span>{pumpConfigs.get(hoveredPump)?.dgb_type || "N/A"}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetMonitor;
